.wrapper {
  position: relative;
  display: inline-flex;
}

/*
 * Deliberately close to unstyled. This component is used as the user menu, as
 * the organization switcher and as a row's overflow control, and those three
 * triggers look nothing alike. The caller passes `triggerClassName`; what is
 * here is only the part that must be true of all of them.
 */
.trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  border-radius: var(--radius-sm);
  color: inherit;
  font: var(--type-ui);
}

.menu {
  position: absolute;
  z-index: var(--z-dropdown);
  min-width: 12rem;
  /* Long menus scroll rather than run off the bottom of the window. The
     organization switcher for a staff member in nine programmes is a real
     case, not a hypothetical one. */
  max-height: min(24rem, 70vh);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: var(--space-1);
  border: 1px solid var(--rule);
  border-radius: var(--radius-panel);
  background: var(--surface-overlay);
  box-shadow: var(--shadow-3);
  animation: menu-in var(--dur-instant) var(--ease-out);
}

@keyframes menu-in {
  from {
    opacity: 0;
    transform: translateY(calc(var(--space-1) * -1));
  }
}

.menu[data-side='bottom'] {
  top: calc(100% + var(--space-1));
}

.menu[data-side='top'] {
  bottom: calc(100% + var(--space-1));
}

.menu[data-align='start'] {
  left: 0;
}

.menu[data-align='end'] {
  right: 0;
}

.item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  gap: var(--space-2);
  width: 100%;
  min-height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border-radius: var(--radius-xs);
  font: var(--type-ui);
  color: var(--text);
  text-align: left;
  transition: background-color var(--dur-instant) var(--ease-out);
}

/*
 * Hover and keyboard focus share one appearance, because in a menu they are
 * one concept: the item the next Enter will act on. Two different highlights
 * moving independently is how a person ends up choosing the wrong one.
 */
.item:hover:not(:disabled),
.item:focus-visible {
  background: var(--hover);
}

.item[aria-checked='true'] {
  background: var(--selected);
}

.item:disabled {
  color: var(--text-disabled);
  cursor: default;
}

.danger {
  color: var(--danger-fg);
}

.itemIcon,
.itemCheck {
  display: grid;
  place-items: center;
  /* Reserved whether or not there is a glyph, so labels line up down the menu
     and a checked item does not shift its neighbours when it is chosen. */
  width: 1rem;
  color: var(--text-muted);
}

.item[aria-checked='true'] .itemCheck {
  color: var(--accent-text);
}

.itemLabel {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.itemNote {
  font: var(--type-micro);
  color: var(--text-faint);
  white-space: nowrap;
}

.separator {
  height: 0;
  margin: var(--space-1) 0;
  border: 0;
  border-top: 1px solid var(--rule-soft);
}

.groupLabel {
  padding: var(--space-2) var(--space-2) var(--space-1);
  font: var(--type-label);
  letter-spacing: var(--tracking-label);
  text-transform: uppercase;
  color: var(--text-faint);
}
